import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  criarHomologacao,
  buscarHomologacao,
  atualizarHomologacao,
  uploadArquivoHomologacao,
  deletarArquivoHomologacao,
  type HomologacaoArquivo,
} from '../api/homologacao';

interface UploadState {
  uploading: boolean;
  progress: number;
}

export function useHomologacao(existingId?: number, urlDefaults?: Record<string, unknown>, clienteId?: number) {
  const [homologacaoId, setHomologacaoId] = useState<number | null>(existingId ?? null);
  const [resolvedClienteId, setResolvedClienteId] = useState<number | null>(clienteId ?? null);
  const [arquivos, setArquivos] = useState<HomologacaoArquivo[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadStates, setUploadStates] = useState<Record<string, UploadState>>({});

  const form = useForm({ defaultValues: urlDefaults || {} });

  // Load existing homologação
  useEffect(() => {
    if (existingId) {
      buscarHomologacao(existingId)
        .then((data) => {
          const dados = typeof data.dados === 'string' ? JSON.parse(data.dados) : data.dados;
          form.reset(dados);
          setHomologacaoId(existingId);
          setArquivos(data.arquivos || []);
          if (data.cliente_id) setResolvedClienteId(data.cliente_id);
        })
        .catch(() => toast.error('Erro ao carregar homologação'));
    }
  }, [existingId]);

  // Save (create or update). silent=true skips toast (for auto-save).
  const salvar = useCallback(async (data?: Record<string, unknown>, silent = false) => {
    setSubmitting(true);
    try {
      const dados = data || form.getValues();
      if (homologacaoId) {
        await atualizarHomologacao(homologacaoId, dados);
        if (!silent) toast.success('Homologação salva');
      } else {
        const { id } = await criarHomologacao(dados, clienteId);
        setHomologacaoId(id);
        if (!silent) toast.success('Homologação criada');
        return id;
      }
      return homologacaoId;
    } catch (err: any) {
      if (!silent) toast.error(err?.response?.data?.message || 'Erro ao salvar');
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [homologacaoId, form, clienteId]);

  // Finalizar (save + set status to 'finalizado')
  const finalizar = useCallback(async (data?: Record<string, unknown>): Promise<boolean> => {
    setSubmitting(true);
    try {
      const dados = data || form.getValues();
      if (homologacaoId) {
        await atualizarHomologacao(homologacaoId, dados, 'finalizado');
      } else {
        const { id } = await criarHomologacao(dados, clienteId);
        setHomologacaoId(id);
        await atualizarHomologacao(id, dados, 'finalizado');
      }
      toast.success('Homologação finalizada!');
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao finalizar');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [homologacaoId, form, clienteId]);

  // Ensure we have an ID (auto-create draft if needed)
  const ensureId = useCallback(async (): Promise<number | null> => {
    if (homologacaoId) return homologacaoId;
    return await salvar();
  }, [homologacaoId, salvar]);

  // Upload file
  const upload = useCallback(async (categoria: string, file: File): Promise<number | null> => {
    const id = await ensureId();
    if (!id) {
      toast.error('Salve a homologação antes de enviar arquivos');
      return null;
    }

    setUploadStates((prev) => ({ ...prev, [categoria]: { uploading: true, progress: 0 } }));
    try {
      const result = await uploadArquivoHomologacao(id, categoria, file, (pct) => {
        setUploadStates((prev) => ({ ...prev, [categoria]: { uploading: true, progress: pct } }));
      });
      // Reload arquivos
      const data = await buscarHomologacao(id);
      setArquivos(data.arquivos || []);
      toast.success(`Arquivo enviado: ${file.name}`);
      return result.id;
    } catch {
      toast.error('Erro ao enviar arquivo');
      return null;
    } finally {
      setUploadStates((prev) => ({ ...prev, [categoria]: { uploading: false, progress: 0 } }));
    }
  }, [ensureId]);

  // Delete file
  const deletar = useCallback(async (arquivoId: number) => {
    try {
      await deletarArquivoHomologacao(arquivoId);
      setArquivos((prev) => prev.filter((a) => a.id !== arquivoId));
      toast.success('Arquivo removido');
      return true;
    } catch {
      toast.error('Erro ao remover arquivo');
      return false;
    }
  }, []);

  // Get arquivos by categoria
  const getArquivosByCategoria = useCallback(
    (categoria: string) => arquivos.filter((a) => a.categoria === categoria),
    [arquivos]
  );

  return {
    form,
    homologacaoId,
    arquivos,
    submitting,
    uploadStates,
    salvar,
    finalizar,
    upload,
    deletar,
    getArquivosByCategoria,
    resolvedClienteId,
  };
}
